/**
 * Static help content for the Help Center panel.
 *
 * Organized into sections with articles, page-specific guides,
 * keyboard shortcuts, and extended tooltip text.
 */

export const helpSections = {
  gettingStarted: {
    id: 'getting-started',
    title: 'Getting Started',
    articles: [
      {
        id: 'what-is-cahoots',
        title: 'What is Cahoots?',
        body: 'Cahoots is an AI-powered planning tool that helps you go from an idea to a ready-to-build engineering plan. Describe what you want to build, and AI will generate features, assumptions, a roadmap, user stories, and an event model.',
      },
      {
        id: 'workflow',
        title: 'How the Workflow Works',
        body: '1. Create an Idea \u2014 Describe your idea in plain language.\n2. Review AI Output \u2014 Check the generated features, assumptions, and specification.\n3. Create a Plan \u2014 Scope an implementation (MVP, full build, etc.).\n4. Get Your Plan \u2014 Receive features, stories, scenarios, and engineering decisions.',
      },
      {
        id: 'first-steps',
        title: 'Your First Steps',
        body: 'Start by clicking "New Idea" on the Dashboard. Write a short description of what you want to build. The AI will generate a complete specification with features, assumptions, and a development roadmap.',
      },
    ],
  },

  keyConcepts: {
    id: 'key-concepts',
    title: 'Key Concepts',
    articles: [
      {
        id: 'concept-product',
        title: 'Ideas',
        body: 'An Idea is the top-level entity in Cahoots. It represents a software concept or system you want to build. Ideas contain features, assumptions, a roadmap, and one or more plans.',
      },
      {
        id: 'concept-feature',
        title: 'Features',
        body: 'Features are specific capabilities your idea needs. They are generated by AI and grouped by priority: Must Have, Should Have, and Could Have. You can edit, add, or remove features.',
      },
      {
        id: 'concept-assumption',
        title: 'Assumptions',
        body: 'Assumptions are decisions the AI made about your idea \u2014 target users, technical constraints, business model, etc. Confirm or change them to refine the specification.',
      },
      {
        id: 'concept-roadmap',
        title: 'Roadmap',
        body: 'A visual plan for your development. Choose from three views: Feature Timeline (Gantt chart), Now/Next/Later (kanban board), or Goal-Oriented (grouped by objectives).',
      },
      {
        id: 'concept-project',
        title: 'Plans',
        body: 'A Plan is a scoped implementation of an Idea. It breaks down into features, user stories, scenarios, and engineering decisions.',
      },
      {
        id: 'concept-feature-group',
        title: 'Features',
        body: 'Large objectives that contain multiple user stories. Each feature represents a significant piece of functionality \u2014 like "User Authentication" or "Payment Processing".',
      },
      {
        id: 'concept-story',
        title: 'User Stories',
        body: 'Specific things a user wants to accomplish, written from the user\'s perspective. For example: "As a user, I want to reset my password so I can regain access to my account."',
      },
      {
        id: 'concept-scenario',
        title: 'Test Scenarios',
        body: 'Given-When-Then test scenarios that validate each user story. They describe the preconditions, action, and expected outcome for a specific behavior.',
      },
      {
        id: 'concept-decision',
        title: 'Decisions',
        body: 'Engineering decisions made during plan creation \u2014 technology choices, architectural patterns, trade-offs. These provide context for why the plan is structured a certain way.',
      },
    ],
  },
};

export const pageGuides = {
  dashboard: {
    title: 'Dashboard',
    description: 'Your central hub for all ideas and recent activity.',
    tips: [
      'Create ideas to describe software concepts you want to build',
      'Click any idea card to see its details and specification',
      'View recent plan activity in the feed on the right',
      'Quick stats show your total idea and plan counts',
    ],
  },
};

export const keyboardShortcuts = [
  { keys: '?', action: 'Open / close Help Center' },
  { keys: 'Esc', action: 'Close modals and panels' },
];

/**
 * Extended tooltip text. Supplements terminology.js tooltips.
 * Used by HelpTooltip component via conceptKey lookup.
 */
export const extendedTooltips = {
  products: 'Software ideas you want to build. Each idea gets AI-generated features, assumptions, and a roadmap.',
  projects: 'Scoped implementations of an idea, broken down into features, stories, and test scenarios.',
  roadmap: 'A visual plan for development phases. Choose Timeline, Now/Next/Later, or Goal-Oriented views.',
  specification: 'The AI-generated document describing your idea\'s features, target users, and technical approach.',
  discovery: 'The process where AI analyzes your idea to create a detailed specification.',
  mvp: 'Minimum Viable Product \u2014 the smallest version that delivers core value to users.',
  granularity: 'Controls how detailed the breakdown is. Fine = more stories, Coarse = fewer larger stories.',
  export: 'Download your plan data or sync it with tools like Jira, Trello, or GitHub.',
};
